import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { formatDate } from '../../utils/dateUtils';
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
  styled,
  useTheme,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PrintIcon from '@mui/icons-material/Print';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  fetchPatientFiles,
  createPatientFile,
  uploadPatientFile,
  updatePatientFile,
  deletePatientFile,
  getPatientFileSignedUrl,
  replacePatientFile,
} from '../../services/patientFileService';

const validationSchema = Yup.object({
  file_name: Yup.string(),
  file_type: Yup.string(),
  file_size: Yup.number().positive().integer(),
  description: Yup.string(),
});

const FILE_DESCRIPTION_OPTIONS = [
  "ΙΣΤΟΡΙΚΟ",
  "ΕΞΕΤΑΣΕΙΣ",
  "ΑΞΟΝΙΚΗ",
  "ΜΑΓΝΗΤΙΚΗ",
  "ΕΝΔΟΣΚΟΠΗΣΗ",
  "ΒΙΟΨΙΑ"
];


const DropZone = styled(Paper)(({ theme, isdragging }) => ({
  border: `2px dashed ${isdragging === 'true' ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3, 2),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  backgroundColor: isdragging === 'true' ? theme.palette.action.hover : theme.palette.background.paper,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

// Lazy-loading thumbnail for the files table
const FileThumbnail = ({ file, apiClient, onClickPreview }) => {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfCanvas, setPdfCanvas] = useState(null);
  const fileId = file?.id_file ?? file?.id;
  const mime = String(file?.file_type || '').toLowerCase();
  const isImage = mime.startsWith('image/');
  const isPdf = mime === 'application/pdf' || mime === 'pdf';

  useEffect(() => {
    if (!fileId || !apiClient || (!isImage && !isPdf)) return;
    let cancelled = false;

    const fetchUrl = async () => {
      setLoading(true);
      try {
        const { url } = await getPatientFileSignedUrl(apiClient, fileId);
        if (cancelled) return;
        setThumbUrl(url);
      } catch {
        // silently fail — thumbnail is best-effort
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUrl();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  // Render PDF page 1 into a canvas thumbnail
  useEffect(() => {
    if (!thumbUrl || !isPdf) return;
    let cancelled = false;

    const renderPdf = async () => {
      let pdfjs = window.pdfjsLib;
      if (!pdfjs) return;
      try {
        const loadingTask = pdfjs.getDocument({ url: thumbUrl, withCredentials: false });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 0.35 });
        const canvas = document.createElement('canvas');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        if (!cancelled) setPdfCanvas(canvas.toDataURL('image/jpeg', 0.7));
        pdf.destroy();
      } catch {
        // silently fail
      }
    };
    renderPdf();
    return () => { cancelled = true; };
  }, [thumbUrl, isPdf]);

  const containerSx = {
    width: 56, height: 56, borderRadius: 1,
    overflow: 'hidden', flexShrink: 0, cursor: 'pointer',
    bgcolor: 'action.selected',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid', borderColor: 'divider',
    '&:hover': { opacity: 0.8 },
    transition: 'opacity 0.15s',
  };

  if (!isImage && !isPdf) return null;

  if (loading) {
    return (
      <Box sx={containerSx} onClick={onClickPreview}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (isPdf && pdfCanvas) {
    return (
      <Box sx={containerSx} onClick={onClickPreview}>
        <img src={pdfCanvas} alt="pdf" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
      </Box>
    );
  }

  if (isPdf) {
    // pdf.js not loaded or rendering failed — show icon
    return (
      <Box sx={{ ...containerSx, bgcolor: '#fff3f3' }} onClick={onClickPreview}>
        <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'error.main', lineHeight: 1, textAlign: 'center', px: 0.5 }}>
          PDF
        </Typography>
      </Box>
    );
  }

  if (isImage && thumbUrl) {
    return (
      <Box sx={containerSx} onClick={onClickPreview}>
        <img src={thumbUrl} alt="thumb" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </Box>
    );
  }

  return null;
};

const FilePreview = ({ url, type, fileName, onOpenNative }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const docs = useMemo(() => [
    { 
      uri: url || '', 
      fileName: fileName || 'Preview',
      fileType: type 
    }
  ], [url, fileName, type]);

  if (!url) return null;

  const mimeType = String(type || '').toLowerCase();
  const isOffice = [
    'application/msword', 
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ].includes(mimeType);

  return (
    <Box sx={{ mt: 2, mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', boxShadow: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle2" fontWeight="bold" color="primary">
          Clinical Document Preview
        </Typography>
        {onOpenNative && (
          <Button 
            size="small" 
            variant="contained" 
            elevation={0}
            startIcon={<FolderOpenIcon />} 
            onClick={onOpenNative}
            sx={{ borderRadius: '20px', textTransform: 'none' }}
          >
            Open in Native App
          </Button>
        )}
      </Stack>

      <Box sx={{ 
        height: '600px', 
        width: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: isDarkMode ? 'action.selected' : '#f5f5f5',
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch'
      }}>
        {isOffice ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 4, textAlign: 'center' }}>
             <Typography variant="h6" color="textPrimary" gutterBottom>
              {mimeType.includes('word') ? 'Word Document' : 'Excel Spreadsheet'}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              Office documents require the native application for the best viewing experience.
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<FolderOpenIcon />} 
              onClick={onOpenNative} 
              size="large"
              sx={{ borderRadius: '8px' }}
            >
              Launch {mimeType.includes('word') ? 'Microsoft Word' : 'Microsoft Excel'}
            </Button>
          </Box>
        ) : mimeType.startsWith('image/') ? (
          <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'transparent' }}>
            <img src={url} alt="Clinical Preview" style={{ maxWidth: '100%', maxHeight: '550px', objectFit: 'contain' }} />
          </Box>
        ) : mimeType.startsWith('audio/') ? (
          <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'transparent', p: 4 }}>
            <audio controls src={url} style={{ width: '100%', maxWidth: '500px' }}>
              Your browser does not support the audio element.
            </audio>
          </Box>
        ) : mimeType.startsWith('video/') ? (
          <Box sx={{ height: '100%', bgcolor: 'black', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
             <video controls src={url} style={{ width: '100%', maxHeight: '100%', maxWidth: '100%' }}>
              Your browser does not support the video element.
            </video>
          </Box>
        ) : mimeType === 'application/pdf' ? (
          <Box sx={{ height: '100%', width: '100%', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
            {window.electron ? (
              // Electron: Show PDF opening options to avoid black screen
              <Box sx={{ 
                flex: 1,
                width: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center', 
                alignItems: 'center', 
                p: 4, 
                textAlign: 'center',
                bgcolor: 'action.hover'
              }}>
                <Typography variant="h6" gutterBottom>
                  PDF Document
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  PDF preview opens in a separate window for better viewing experience.
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button 
                    variant="contained" 
                    onClick={() => window.open(url, '_blank', 'width=900,height=700,menubar=yes,toolbar=yes')}
                    startIcon={<VisibilityIcon />}
                  >
                    Open PDF
                  </Button>
                  <Button 
                    variant="outlined" 
                    component="a"
                    href={url}
                    download={fileName}
                    startIcon={<DownloadIcon />}
                  >
                    Download
                  </Button>
                </Stack>
              </Box>
            ) : (
              // Web: Use iframe with sandbox attributes
              <iframe 
                src={`${url}#toolbar=1&view=FitH&navpanes=0`} 
                title="PDF Preview"
                width="100%" 
                height="100%" 
                style={{ border: 'none', display: 'block', flex: 1 }}
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
                allow="fullscreen"
              />
            )}
          </Box>
        ) : (
          <Box sx={{ height: '100%', width: '100%' }}>
            <DocViewer 
              documents={docs} 
              pluginRenderers={DocViewerRenderers}
              style={{ height: '100%' }}
              config={{
                header: {
                  disableHeader: false,
                  disableFileName: false,
                  retainURLParams: true
                }
              }}
              theme={{
                primary: theme.palette.primary.main,
                secondary: theme.palette.background.paper,
                tertiary: theme.palette.action.hover,
                textPrimary: theme.palette.text.primary,
                textSecondary: theme.palette.text.secondary,
                textTertiary: theme.palette.text.disabled,
                disableThemeScrollbar: false,
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

  const resolveFileId = (file) => {
    const id = file?.id_file ?? file?.id;
    return id ? String(id) : null;
  };

const getFileTypeLabel = (mimeType) => {
  const t = String(mimeType || '').toLowerCase();
  if (t === 'application/pdf' || t === 'pdf') return 'PDF';
  if (t === 'application/msword' || t.includes('word') || t.includes('officedocument.word')) return 'WORD';
  if (t === 'application/vnd.ms-excel' || t.includes('excel') || t.includes('spreadsheet') || t.includes('ms-excel')) return 'EXCEL';
  if (t.startsWith('image/')) return 'IMAGE';
  if (t.startsWith('video/')) return 'VIDEO';
  if (t.startsWith('audio/')) return 'AUDIO';
  if (t.startsWith('text/')) return 'TEXT';
  return t.split('/')[1]?.toUpperCase() || t.toUpperCase() || 'FILE';
};

const PatientFilesTab = ({ patientId, apiClient, onFilesChanged, onPreviewRequest }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [replacementFile, setReplacementFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [signedUrlMap, setSignedUrlMap] = useState({});
  const [signingIds, setSigningIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const loadFiles = useCallback(async () => {
    if (!apiClient || !patientId) return;

    setLoading(true);
    setError('');

    try {
      const data = await fetchPatientFiles(apiClient, patientId);
      setFiles(data);
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [apiClient, patientId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleAdd = () => {
    setEditingFile(null);
    setSelectedFile(null);
    setReplacementFile(null);
    setDialogOpen(true);
  };

  const handleEdit = async (file) => {
    setEditingFile(file);
    setSelectedFile(null);
    setReplacementFile(null);
    setLocalPreviewUrl(null);
    setDialogOpen(true);

    // Fetch signed URL for preview in the edit dialog
    if (file) {
      fetchSignedUrl(file);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedFile(null);
    setReplacementFile(null);
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await deletePatientFile(apiClient, id);
      setFiles((prev) => {
        const next = prev.filter((f) => resolveFileId(f) !== id);
        onFilesChanged?.();
        return next;
      });
      onPreviewRequest?.(null);
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to delete file');
    }
  };

  const fetchSignedUrl = async (file) => {
    const id = resolveFileId(file);
    if (!id) return null;

    // Return cached result immediately — avoids redundant requests and race conditions
    const cached = signedUrlMap[id];
    if (cached) return cached;

    // Deduplicate concurrent in-flight requests for the same file
    if (signingIds.has(id)) {
      // Wait for the in-flight request to finish by polling the cache
      return new Promise((resolve) => {
        const poll = setInterval(() => {
          const result = signedUrlMap[id];
          if (result !== undefined) {
            clearInterval(poll);
            resolve(result);
          }
        }, 100);
        // Timeout after 10 seconds
        setTimeout(() => { clearInterval(poll); resolve(null); }, 10000);
      });
    }

    setSigningIds((prev) => new Set(prev).add(id));
    try {
      const { url, file_type: fileType } = await getPatientFileSignedUrl(apiClient, id);
      const result = { url, file_type: fileType };
      setSignedUrlMap((prev) => ({
        ...prev,
        [id]: result
      }));
      return result;
    } catch (err) {
      console.error('Failed to fetch signed URL:', err);
      return null;
    } finally {
      setSigningIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleDownload = async (file) => {
    try {
      if (window.electron) {
        // In Electron, use the file path directly
        const filePath = file.file_path || file.storage_path;
        if (!filePath) {
          throw new Error('File path not available');
        }
        
        // Show the file in the file explorer
        window.electron.ipcRenderer.send('show-file-in-folder', filePath);
        return;
      }
      
      // Web version - use signed URLs
      const signed = await fetchSignedUrl(file);
      const url = signed?.url ?? file.file_url;
      if (!url) {
        throw new Error('Unable to resolve file URL');
      }
      
      const link = document.createElement('a');
      link.href = url;
      link.download = file.file_name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(err.message ?? 'Failed to download file');
    }
  };

  const handlePreview = async (file) => {
    try {
      if (window.electron) {
        // In Electron, use the file path directly
        const filePath = file.file_path || file.storage_path;
        if (!filePath) {
          throw new Error('File path not available');
        }
        
        // Notify parent of the selection first so the sidebar can track it
        onPreviewRequest?.(file);
        
        // Open the file using Electron's shell
        window.electron.ipcRenderer.send('open-file', filePath);
        return;
      }
      
      // Web version - use signed URLs (fetchSignedUrl now caches results)
      const signed = await fetchSignedUrl(file);
      if (!signed?.url) {
        throw new Error('Unable to resolve file URL. Please try again.');
      }
      
      const enrichedFile = {
        ...file,
        signedUrl: signed.url,
        // Normalise: prefer the API-returned type, fall back to the stored type
        signedFileType: signed.file_type ?? file.file_type,
      };
      setPreviewFile(enrichedFile);
      onPreviewRequest?.(enrichedFile);
    } catch (err) {
      setError(err.message ?? 'Failed to open file');
    }
  };

  const handleFileChange = (file, setFieldValue) => {
    if (!file) return;
    
    // Create local preview
    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    const url = URL.createObjectURL(file);
    setLocalPreviewUrl(url);

    if (editingFile) {
      setReplacementFile(file);
    } else {
      setSelectedFile(file);
      setFieldValue('file_name', file.name);
      setFieldValue('file_type', file.type || file.name.split('.').pop().toLowerCase());
      setFieldValue('file_size', file.size);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e, setFieldValue) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileChange(files[0], setFieldValue);
    }
  };

  const handleFileInputClick = (e) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e, setFieldValue) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileChange(file, setFieldValue);
    }
  };

  const openPreviewFullscreen = () => {
    if (!previewFile?.signedUrl) return;
    setIsPreviewFullscreen(true);
  };

  const closePreviewFullscreen = () => {
    setIsPreviewFullscreen(false);
  };

  const handlePreviewDownload = () => {
    if (!previewFile?.signedUrl) return;
    window.open(previewFile.signedUrl, '_blank');
  };

  const handlePreviewPrint = () => {
    if (!previewFile?.signedUrl) return;
    const printWindow = window.open(previewFile.signedUrl, '_blank');
    if (!printWindow) return;
    const handlePrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
      } catch (err) {
        console.error('Print failed', err);
      }
    };
    if (printWindow.document?.readyState === 'complete') {
      handlePrint();
    } else {
      const handlePrintOnLoad = () => {
        handlePrint();
        printWindow.removeEventListener('load', handlePrintOnLoad);
      };
      printWindow.addEventListener('load', handlePrintOnLoad);
    }
  };

  const closePreview = () => {
    closePreviewFullscreen();
    setPreviewFile(null);
  };

  const handleSubmit = async (values, { setStatus }) => {
    setSubmitting(true);
    setStatus(undefined);

    try {
      if (!editingFile) {
        // Handle new file upload
        if (!selectedFile) {
          setStatus('Please select a file to upload');
          setSubmitting(false);
          return;
        }

        const formData = new FormData();
        formData.append('patientId', patientId);
        
        // In Electron, we need to create a new File object from the file path
        let fileToUpload = selectedFile;
        if (window.electron && selectedFile.path) {
          try {
            const fileContent = await window.electron.fs.readFile(selectedFile.path);
            fileToUpload = new File([fileContent], selectedFile.name || 'file', {
              type: selectedFile.type || 'application/octet-stream',
            });
          } catch (err) {
            console.error('Error reading file in Electron:', err);
            throw new Error('Failed to read file. Please try again.');
          }
        }
        
        formData.append('file', fileToUpload);
        if (values.file_name) {
          formData.append('fileName', values.file_name);
        }
        if (values.description) {
          formData.append('description', values.description);
        }
        if (values.storage_path) {
          formData.append('storage_path', values.storage_path);
        }

        const uploaded = await uploadPatientFile(apiClient, patientId, formData);
        setFiles((prev) => {
          const next = [uploaded, ...prev];
          onFilesChanged?.();
          return next;
        });
      } else {
        // Handle file update or replacement
        const targetId = resolveFileId(editingFile);
        if (replacementFile) {
          const formData = new FormData();
          
          // In Electron, handle file reading from path
          let fileToUpload = replacementFile;
          if (window.electron && replacementFile.path) {
            try {
              const fileContent = await window.electron.fs.readFile(replacementFile.path);
              fileToUpload = new File([fileContent], replacementFile.name || 'file', {
                type: replacementFile.type || 'application/octet-stream',
              });
            } catch (err) {
              console.error('Error reading replacement file in Electron:', err);
              throw new Error('Failed to read replacement file. Please try again.');
            }
          }
          
          formData.append('file', fileToUpload);
          // Make sure to include patientId in the form data for the server
          formData.append('patientId', values.id_pt || patientId);
          if (values.description) {
            formData.append('description', values.description);
          }
          if (values.storage_path) {
            formData.append('storage_path', values.storage_path);
          }

          const updated = await replacePatientFile(apiClient, patientId, targetId, formData);
          setFiles((prev) => {
            const next = prev.map((f) => (resolveFileId(f) === resolveFileId(updated) ? updated : f));
            onFilesChanged?.();
            return next;
          });
          setSignedUrlMap((prev) => {
            const next = { ...prev };
            delete next[targetId];
            return next;
          });
          onPreviewRequest?.({ ...updated, signedUrl: null });
        } else {
          // Handle metadata update only
          const payload = {
            description: values.description || null,
            file_type: values.file_type || null,
            file_size: values.file_size ? Number(values.file_size) : null,
          };

          const updated = await updatePatientFile(apiClient, targetId, payload);
          setFiles((prev) => {
            const next = prev.map((f) => (resolveFileId(f) === resolveFileId(updated) ? updated : f));
            onFilesChanged?.();
            return next;
          });
        }
      }

      setDialogOpen(false);
      setSelectedFile(null);
      setReplacementFile(null);
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setStatus(err.response?.data?.error ?? err.message ?? 'Failed to save file');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    const size = typeof bytes === 'string' ? Number(bytes) : bytes;
    if (!size || Number.isNaN(size)) return 'Unknown size';
    const kb = size / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const initialValues = editingFile
    ? {
        id_file: resolveFileId(editingFile) ?? '',
        id_pt: editingFile.id_pt ?? '',
        file_name: editingFile.file_name ?? editingFile.filename ?? '',
        file_type: editingFile.file_type ?? '',
        file_size: editingFile.file_size ? String(editingFile.file_size) : '',
        file_url: editingFile.file_url ?? editingFile.fileurl ?? '',
        storage_path: editingFile.storage_path ?? '',
        upload_date: editingFile.upload_date ?? '',
        created_at: editingFile.created_at ?? '',
        updated_at: editingFile.updated_at ?? '',
        description: editingFile.description ?? '',
      }
    : {
        id_file: '',
        id_pt: patientId ?? '',
        file_name: '',
        file_type: '',
        file_size: '',
        file_url: '',
        storage_path: '',
        upload_date: '',
        created_at: '',
        updated_at: '',
        description: '',
      };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Patient Files</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Add File
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {files.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No files uploaded yet
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 72 }}>Preview</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((file) => {
                const fileId = resolveFileId(file);
                const isDisabled = !fileId || signingIds.has(fileId);
                const isElectron = window.electron;
                
                return (
                  <TableRow key={fileId ?? Math.random()} hover>
                    {isElectron ? (
                      // Electron-specific UI
                      <>
                        <TableCell sx={{ py: 0.75 }}>
                          <FileThumbnail file={file} apiClient={apiClient} onClickPreview={() => handlePreview(file)} />
                        </TableCell>
                        <TableCell>{file.filename || file.file_name || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.description || '—'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getFileTypeLabel(file.file_type)} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>{formatFileSize(file.file_size)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {file.created_at ? formatDate(file.created_at) : '—'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {file.updated_at ? formatDate(file.updated_at) : '—'}
                        </TableCell>
                      </>
                    ) : (
                      // Web UI
                      <>
                        <TableCell sx={{ py: 0.75 }}>
                          <FileThumbnail file={file} apiClient={apiClient} onClickPreview={() => handlePreview(file)} />
                        </TableCell>
                        <TableCell>{file.filename || file.file_name || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.description || '—'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getFileTypeLabel(file.file_type)} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>{formatFileSize(file.file_size)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {file.created_at ? formatDate(file.created_at) : '—'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {file.updated_at ? formatDate(file.updated_at) : '—'}
                        </TableCell>
                      </>
                    )}
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {isElectron ? (
                          // Electron action buttons
                          <>
                            <Tooltip title="Open in default application">
                              <IconButton
                                size="small"
                                onClick={() => handlePreview(file)}
                                disabled={isDisabled}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownload(file)}
                                disabled={isDisabled}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(file)}
                                disabled={isDisabled}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(fileId)}
                                disabled={isDisabled}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          // Web action buttons (unchanged)
                          <>
                            <Tooltip title="Preview">
                              <span>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => handlePreview(file)}
                                  disabled={isDisabled}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Download">
                              <span>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => handleDownload(file)}
                                  disabled={isDisabled}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <span>
                                <IconButton edge="end" size="small" onClick={() => handleEdit(file)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <span>
                                <IconButton edge="end" size="small" onClick={() => handleDelete(fileId)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingFile ? 'Edit File' : 'Add File'}</DialogTitle>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, touched, errors, handleBlur, status, setFieldValue }) => {
            // Determine preview URL and type
            let previewUrl = localPreviewUrl;
            let previewType = editingFile ? (editingFile.file_type ?? editingFile.filetype) : (selectedFile?.type);
            let previewName = editingFile ? (editingFile.file_name ?? editingFile.filename) : (selectedFile?.name);

            if (!previewUrl && editingFile) {
              const signed = signedUrlMap[resolveFileId(editingFile)];
              if (signed) {
                previewUrl = signed.url;
                previewType = signed.file_type || previewType;
              }
            }

            return (
              <Form>
                <DialogContent dividers>
                  <Grid container spacing={3}>
                    {/* File Selection & Preview Section */}
                    <Grid item xs={12} md={6}>
                      <Stack spacing={2}>
                        <Typography variant="subtitle2" color="textSecondary">File Selection</Typography>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={(e) => handleFileInputChange(e, setFieldValue)}
                          hidden
                          accept="*/*"
                        />
                        <DropZone
                          isdragging={isDragging.toString()}
                          onClick={handleFileInputClick}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, setFieldValue)}
                          sx={{ py: 4 }}
                        >
                          <Stack spacing={1} alignItems="center">
                            <AddIcon color="primary" sx={{ fontSize: 40 }} />
                            <Typography variant="body2" color="textSecondary">
                              {editingFile ? 'Click to replace file' : 'Drag & drop or Click to upload'}
                            </Typography>
                          </Stack>
                        </DropZone>

                        {/* File Metadata Info */}
                        {(selectedFile || editingFile || replacementFile) && (
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                            <Stack spacing={1}>
                              <Box display="flex" justifyContent="space-between">
                                <Typography variant="caption" color="textSecondary">Name:</Typography>
                                <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: '200px' }}>
                                  {replacementFile?.name || values.file_name}
                                </Typography>
                              </Box>
                              <Box display="flex" justifyContent="space-between">
                                <Typography variant="caption" color="textSecondary">Type:</Typography>
                                <Chip 
                                  label={String(replacementFile?.type || values.file_type || 'unknown').toUpperCase()} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              </Box>
                              <Box display="flex" justifyContent="space-between">
                                <Typography variant="caption" color="textSecondary">Size:</Typography>
                                <Typography variant="caption">
                                  {formatFileSize(replacementFile?.size || values.file_size)}
                                </Typography>
                              </Box>
                              {values.upload_date && (
                                <Box display="flex" justifyContent="space-between">
                                  <Typography variant="caption" color="textSecondary">Uploaded:</Typography>
                                  <Typography variant="caption">{new Date(values.upload_date).toLocaleString('el-GR')}</Typography>
                                </Box>
                              )}
                              {values.created_at && (
                                <Box display="flex" justifyContent="space-between">
                                  <Typography variant="caption" color="textSecondary">Created:</Typography>
                                  <Typography variant="caption">{new Date(values.created_at).toLocaleString('el-GR')}</Typography>
                                </Box>
                              )}
                              {values.updated_at && (
                                <Box display="flex" justifyContent="space-between">
                                  <Typography variant="caption" color="textSecondary">Updated:</Typography>
                                  <Typography variant="caption">{new Date(values.updated_at).toLocaleString('el-GR')}</Typography>
                                </Box>
                              )}
                              {values.storage_path && (
                                <Box display="flex" flexDirection="column" gap={0.5} sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" color="textSecondary">Storage Path:</Typography>
                                  <Typography variant="caption" sx={{ wordBreak: 'break-all', bgcolor: 'action.selected', p: 0.5, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                    {values.storage_path}
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          </Paper>
                        )}
                      </Stack>
                    </Grid>

                    {/* Preview & Description Section */}
                    <Grid item xs={12} md={6}>
                      <Stack spacing={2} sx={{ height: '100%' }}>
                        <Typography variant="subtitle2" color="textSecondary">Details & Preview</Typography>
                        
                        <Autocomplete
                          freeSolo
                          options={FILE_DESCRIPTION_OPTIONS}
                          value={values.description}
                          onInputChange={(event, newValue) => {
                            setFieldValue('description', newValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="description"
                              label="Description / Category"
                              fullWidth
                              multiline
                              rows={3}
                              placeholder="Select a category or type details..."
                              onBlur={handleBlur}
                              error={touched.description && Boolean(errors.description)}
                              helperText={touched.description && errors.description}
                            />
                          )}
                        />

                        {previewUrl ? (
                          <FilePreview 
                            url={previewUrl} 
                            type={previewType} 
                            fileName={previewName} 
                            onOpenNative={() => {
                              const filePath = editingFile?.file_path || editingFile?.storage_path;
                              if (filePath && window.electron) {
                                window.electron.ipcRenderer.send('open-file', filePath);
                              }
                            }}
                          />
                        ) : editingFile ? (
                          <Box sx={{ py: 8, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                            <Button 
                              size="small" 
                              onClick={() => fetchSignedUrl(editingFile)}
                              disabled={signingIds.has(resolveFileId(editingFile))}
                              sx={{ color: 'primary.main' }}
                            >
                              {signingIds.has(resolveFileId(editingFile)) ? 'Loading Preview...' : 'Load Preview'}
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ py: 8, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                            <Typography variant="caption" color="textSecondary">No file selected for preview</Typography>
                          </Box>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>

                  {status && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {status}
                    </Alert>
                  )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                  <Button onClick={handleCloseDialog} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    disabled={submitting || (!editingFile && !selectedFile)}
                    startIcon={submitting ? <CircularProgress size={20} /> : null}
                  >
                    {submitting ? 'Saving...' : editingFile ? 'Update Details' : 'Upload File'}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>

      <Dialog
        open={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: '92vh', maxHeight: '92vh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          p: 2
        }}>
          <Typography variant="h6" noWrap>
            {previewFile?.file_name ?? previewFile?.file_url}
          </Typography>
          <Box>
            <Tooltip title="Download">
              <IconButton onClick={handlePreviewDownload} size="small" sx={{ ml: 1 }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton onClick={handlePreviewPrint} size="small" sx={{ ml: 1 }}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton onClick={() => setPreviewFile(null)} size="small" sx={{ ml: 1 }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          {previewFile?.signedUrl ? (
            previewFile?.signedFileType?.startsWith('image/') ? (
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                <Box
                  component="img"
                  src={previewFile.signedUrl}
                  alt={previewFile.file_name ?? 'Patient file preview'}
                  sx={{ 
                    maxWidth: '100%', 
                    maxHeight: 'calc(90vh - 150px)',
                    objectFit: 'contain',
                    borderRadius: 1,
                    boxShadow: 1
                  }}
                />
              </Box>
            ) : (() => {
              const pdfType = previewFile?.signedFileType || previewFile?.file_type || '';
              const isPdf = String(pdfType).toLowerCase() === 'application/pdf' || String(pdfType).toLowerCase() === 'pdf';
              if (isPdf) {
                return (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
                    {window.electron ? (
                      <Box sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        p: 4, 
                        textAlign: 'center',
                        bgcolor: 'action.hover'
                      }}>
                        <Typography variant="h6" gutterBottom>
                          PDF Document: {previewFile.file_name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                          PDF preview opens in a separate window for better viewing experience.
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          <Button 
                            variant="contained" 
                            onClick={() => window.open(previewFile.signedUrl, '_blank', 'width=900,height=700,menubar=yes,toolbar=yes')}
                            startIcon={<VisibilityIcon />}
                          >
                            Open PDF
                          </Button>
                          <Button 
                            variant="outlined" 
                            component="a"
                            href={previewFile.signedUrl}
                            download={previewFile.file_name}
                            startIcon={<DownloadIcon />}
                          >
                            Download
                          </Button>
                        </Stack>
                      </Box>
                    ) : (
                      <embed
                        src={`${previewFile.signedUrl}#toolbar=1&view=FitH&navpanes=0&scrollbar=1`}
                        type="application/pdf"
                        style={{ flex: 1, width: '100%', border: 'none', display: 'block', minHeight: 0, height: '100%' }}
                      />
                    )}
                  </Box>
                );
              }
              return (
                <Box sx={{ p: 3 }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Preview is not available for this file type ({pdfType || 'unknown'}). Use the download button to view it.
                  </Typography>
                  <Button variant="contained" onClick={() => handleDownload(previewFile)}>
                    Download
                  </Button>
                </Box>
              );
            })()
          ) : (
            <Typography variant="body2">No preview available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewFile(null)}>Close</Button>
          {previewFile?.signedUrl && (
            <Button variant="contained" onClick={() => handleDownload(previewFile)}>
              Open in New Tab
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientFilesTab;
